
import { computed, defineComponent, PropType, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import MixSelectors from '@/views/dashboard/_components/group-policy-settings/_components/mix-selectors/mix-selectors.vue'
import ContentAdaptLayout from '../../../../_components/content-adapt-layout/content-adapt-layout.vue'
import { BaselineScanCustomItem, BaselineScanSettings, GroupPolicySettingsService, GroupPolicyTypes } from '../../group-policy-settings.service'
import ScanItemDrawer from '@/views/dashboard/_components/group-policy-settings/_components/baseline-scan/scan-item-drawer.vue'
import { AllBaseLineItemResponse } from '@/views/dashboard/safe-status/base-line/repair-item-tree-type'
import {
  BaselineScanService
} from '@/views/dashboard/_components/group-policy-settings/_components/baseline-scan/baseline-scan-service'
import { OperateType } from '@/views/dashboard/_components/group-policy-settings/group-policy-drawer.service'
import DetailsSubscribeBar from '@/views/dashboard/_components/subscribe-bar/details-subscribe-bar.vue'
import { TerminalType } from '@/views/dashboard/_enum/terminal-type'
import { PermissionFe, ServicePermissionService } from '@/services/service-permission.service'
const DEFAULT_PARAMS = {
  main_switch: false,
  frequency: 1,
  scan_item_custom: [] as BaselineScanCustomItem[],
}

const ModelOptions = {
  start: 'start',
  week: 'week',
  month: 'month'
}
const BaselinePolicyType = {
  1: GroupPolicyTypes.BaselineScan,
  2: GroupPolicyTypes.LinuxBaselineScan
}
export default defineComponent({
  components: {
    DetailsSubscribeBar,
    ContentAdaptLayout,
    MixSelectors,
    ScanItemDrawer
  },
  props: {
    operateType: {
      type: String as PropType<'edit'|'add'|'read'>,
      default: 'edit'
    },
    policyId: {
      type: String,
      default: undefined
    },
    name: {
      type: String,
      default: ''
    },
    activeName: {
      type: String,
      default: ''
    },
    systemTab: {
      type: String,
      default: '1'
    }
  },
  setup (props, context) {
    const { t } = useI18n()
    const injector = useReflectiveInjector([BaselineScanService])
    const servicePermissionService = injector.get(ServicePermissionService)
    const groupPolicySettingsService = injector.get(GroupPolicySettingsService)
    const repairService = injector.get(BaselineScanService)
    const loading = ref<boolean>(true)

    const data = reactive({
      hasEdit: false,
      scanItemDrawer: false,
      scanItemSelectCount: 37,
      totalItemsCount: 37,
      scanType: -1,
      scan_item_ids: []
    })
    const strategy = reactive<BaselineScanSettings>({
      ...DEFAULT_PARAMS
    })

    const hasPermission = computed(() => {
      if (+props.systemTab === TerminalType.linux) {
        return servicePermissionService.getHasPermission(PermissionFe.linux_base_auth)
      }
      return true
    })

    function getSettings () {
      repairService.getAllBaselineItems().then((res: AllBaseLineItemResponse) => {
        const itemCount = props.systemTab === '1' ? res.nums : res.linux_nums
        data.totalItemsCount = data.scanItemSelectCount = itemCount
      })
      groupPolicySettingsService.getSettings({
        id: props.policyId,
        type: BaselinePolicyType[props.systemTab]
      }).then(response => {
        Object.assign(strategy, {
          ...DEFAULT_PARAMS,
        }, response.strategy)
        groupPolicySettingsService.updateSelectors('baseLineSelectors', (response.strategy as BaselineScanSettings).regular_tasks)
        data.scanType = (response.strategy as BaselineScanSettings).scan_type
        groupPolicySettingsService.customBaselineScanItemKeys = data.scan_item_ids = (response.strategy as BaselineScanSettings).scan_item_ids || []
        if ((response.strategy as BaselineScanSettings).scan_item_ids) {
          data.scanItemSelectCount = (response.strategy as BaselineScanSettings).scan_item_ids.filter(key => !['1', '2', '3', '4', '100', '101', '102', '103', '104'].includes(key)).length
        }
        if (!strategy.frequency) {
          strategy.frequency = 1
          // strategy.regular_tasks = [{ target: 1, start_min: 0, end_min: 10 }]
        }
      }).finally(() => {
        loading.value = false
      })
    }

    function saveSettings () {
      strategy.scan_type = data.scanType
      strategy.scan_item_ids = data.scan_item_ids.filter(id => !['1', '2', '3', '4', '100', '101', '102', '103', '104'].includes(id))
      groupPolicySettingsService.saveSettings({
        id: props.policyId,
        type: BaselinePolicyType[props.systemTab],
        strategy: JSON.stringify(strategy)
      }).then(() => {
        groupPolicySettingsService.updateEditTab(false)
        context.emit('save')
      })
    }
    function cancel () {
      context.emit('cancel')
    }
    groupPolicySettingsService.baseLineScanType.subscribe((type: number) => {
      data.scanType = type
    })
    groupPolicySettingsService.baselineScanItemKeys.subscribe((keys: string[]) => {
      data.scan_item_ids = keys
      data.scanItemSelectCount = keys.filter(key => !['1', '2', '3', '4', '100', '101', '102', '103', '104'].includes(key)).length
    })
    groupPolicySettingsService.baselineScanCustomValues.subscribe((items) => {
      strategy.scan_item_custom = items
    })
    groupPolicySettingsService.baseLineSelectors.subscribe((res: any) => {
      strategy.regular_tasks = res
    })
    groupPolicySettingsService.hasEditTab.subscribe((hasEdit: boolean) => {
      data.hasEdit = hasEdit
    })
    groupPolicySettingsService.saveTab.subscribe((name: string) => {
      if (name === 'baseline-scan') {
        saveSettings()
      }
    })
    function setChangeStatus () {
      groupPolicySettingsService.updateEditTab(true)
    }

    watch(() => props.activeName, (newV) => {
      if (newV === 'baseline-scan') {
        getSettings()
      }
    })

    getSettings()

    return {
      t,
      TerminalType,
      PermissionFe,
      OperateType,
      data,
      loading,
      hasPermission,
      strategy,
      ModelOptions,
      setChangeStatus,
      cancel,
      openScanItemDrawer () {
        data.hasEdit = true
        data.scanItemDrawer = true
      },
      handleScanDrawerClose () {
        data.scanItemDrawer = false
      },
      handleSaveBaselineScanSetting () {
        saveSettings()
      },
      changeFrequency () {
        setChangeStatus()
        groupPolicySettingsService.updateSelectors('baseLineSelectors', [{ target: 1, start_min: 0, end_min: 10 }])
      }
    }
  },
})
